import React from "react";
import { Box, Button, Typography, TextField, IconButton } from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import { DistinctValue } from "../../../types";
import { EntityFieldItem } from "../../../services/cleaning-data-service";
import FieldAutocomplete from "../../shared/field-autocomplete";
import { useSplitValues } from "./hooks/use-split-values";

interface SplitValuesFooterProps {
  fieldId: number | undefined;
  selectedItem: DistinctValue | null;
  refreshDistinctValues: () => void;
  availableFields: EntityFieldItem[] | undefined;
  isLoadingFields: boolean;
}

/**
 * Footer for Split Values category (categoryId: 7)
 */
const SplitValuesFooter: React.FC<SplitValuesFooterProps> = ({
  fieldId,
  selectedItem,
  refreshDistinctValues,
  availableFields,
  isLoadingFields,
}) => {
  const {
    splitEntries,
    handleAddSplit,
    handleRemoveSplit,
    handleValueChange,
    handleFieldChange,
    handleSplit,
    handleCancel,
    handleCopyValue,
  } = useSplitValues({
    fieldId,
    selectedItem,
    availableFields,
    refreshDistinctValues,
  });

  if (!selectedItem) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          borderTop: "1px solid #eee",
          p: 2,
        }}
      >
        <Typography variant="caption" color="text.secondary">
          Select a value to split into multiple parts.
        </Typography>
      </Box>
    );
  }

  return (
    <Box>
      <Box
        sx={{
          borderTop: "1px solid #eee",
          p: 2,
          maxHeight: "300px",
          overflowY: "auto",
        }}
      >
        <Box
          sx={{
            mb: 2,
            display: "flex",
            alignItems: "center",
            gap: 1,
          }}
        >
          <Typography variant="caption" color="text.secondary">
            Split &quot;{selectedItem.value}&quot;{" "}
            <IconButton
              onClick={handleCopyValue}
              size="small"
              color="primary"
              title="Copy value to clipboard"
              sx={{ p: 0.5 }}
            >
              <ContentCopyIcon sx={{ fontSize: 16 }} />
            </IconButton>
            into:
          </Typography>
        </Box>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
          {splitEntries.map((entry, index) => (
            <Box
              key={entry.id}
              sx={{
                display: "flex",
                gap: 1,
                alignItems: "center",
              }}
            >
              <TextField
                label={`Value ${index + 1}`}
                value={entry.newValue}
                onChange={(e) => handleValueChange(entry.id, e.target.value)}
                size="small"
                fullWidth
                sx={{ flex: 1 }}
              />
              <Box sx={{ flex: 1, minWidth: 200 }}>
                <FieldAutocomplete
                  options={availableFields || []}
                  value={entry.targetField}
                  onFieldChange={(field) => handleFieldChange(entry.id, field)}
                  disabled={isLoadingFields || index === 0}
                  sx={
                    index === 0
                      ? {
                          "& .MuiInputBase-input.Mui-disabled": {
                            WebkitTextFillColor: "rgba(0, 0, 0, 0.87)",
                          },
                        }
                      : undefined
                  }
                />
              </Box>
              <IconButton
                onClick={() => handleRemoveSplit(entry.id)}
                disabled={splitEntries.length === 2}
                size="small"
                color="error"
              >
                <DeleteIcon />
              </IconButton>
            </Box>
          ))}
        </Box>
        <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 2 }}>
          <Button
            startIcon={<AddIcon />}
            onClick={handleAddSplit}
            size="small"
            variant="outlined"
          >
            Add Split Value
          </Button>
        </Box>
      </Box>
      <Box
        sx={{
          display: "flex",
          justifyContent: "flex-end",
          alignItems: "center",
          borderTop: "1px solid #eee",
          p: 2,
        }}
      >
        <Button
          variant="contained"
          color="primary"
          size="small"
          sx={{ mr: 1, borderRadius: 1 }}
          onClick={handleSplit}
          disabled={splitEntries.length === 0}
        >
          Split
        </Button>
        <Button
          variant="outlined"
          size="small"
          sx={{ borderRadius: 1 }}
          onClick={handleCancel}
        >
          Cancel
        </Button>
      </Box>
    </Box>
  );
};

export default SplitValuesFooter;
